import { useCallback, useEffect, useRef, useState } from 'react';
import { isDesktop, isMobileOnly, isTablet } from 'react-device-detect';

/**
 * Hook personalizado para detectar diferentes breakpoints de pantalla
 * Incluye lógica personalizada para considerar pantallas < 1180px como tablet/mobile
 */
export const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(() => {
    // Inicialización segura para SSR
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 1180; // Valor por defecto para SSR
  });

  const [isInitialized, setIsInitialized] = useState(() => {
    // Marcar como inicializado si window está disponible
    return typeof window !== 'undefined';
  });
  const [forceUpdate, setForceUpdate] = useState(0);
  const timeoutRef = useRef(null);
  const previousBreakpointRef = useRef(null);

  const updateScreenWidth = useCallback(() => {
    if (typeof window === 'undefined') return;

    const newWidth = window.innerWidth;
    const previousWidth = screenWidth;

    // Solo actualizar si realmente cambió
    if (newWidth === previousWidth) return;

    setScreenWidth(newWidth);

    // Asegurar que esté marcado como inicializado
    if (!isInitialized) {
      setIsInitialized(true);
    }

    // Detectar cambios de breakpoint críticos
    const currentBreakpoint = newWidth >= 1180 ? 'desktop' : newWidth >= 768 ? 'tablet' : 'mobile';
    const previousBreakpoint =
      previousWidth >= 1180 ? 'desktop' : previousWidth >= 768 ? 'tablet' : 'mobile';

    // Forzar actualización cuando se cruzan breakpoints críticos
    const crossedDesktopBreakpoint =
      (previousBreakpoint !== 'desktop' && currentBreakpoint === 'desktop') ||
      (previousBreakpoint === 'desktop' && currentBreakpoint !== 'desktop');

    const crossedTabletBreakpoint =
      (previousBreakpoint !== 'tablet' && currentBreakpoint === 'tablet') ||
      (previousBreakpoint === 'tablet' && currentBreakpoint !== 'tablet');

    if (crossedDesktopBreakpoint || crossedTabletBreakpoint) {
      setForceUpdate((prev) => prev + 1);
      previousBreakpointRef.current = currentBreakpoint;

      // Si se pasa de desktop a tablet/móvil, limpiar off-canvas
      if (previousBreakpoint === 'desktop' && currentBreakpoint !== 'desktop') {
        // Importar dinámicamente para evitar dependencias circulares
        import('@/utils/infrastructure').then(({ hideAllOffCanvas }) => {
          hideAllOffCanvas();
        });
      }
    }
  }, [screenWidth, isInitialized]);

  useEffect(() => {
    // Establecer valor inicial inmediatamente
    updateScreenWidth();

    // Debounced listener para evitar múltiples ejecuciones
    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        updateScreenWidth();
      }, 16); // ~60fps
    };

    // Agregar listener para cambios de tamaño
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [updateScreenWidth]);

  // Lógica personalizada basada en screenWidth
  const isCustomDesktop = screenWidth >= 1180;
  const isCustomTabletOrMobile = screenWidth < 1180;

  // Usar SIEMPRE nuestra lógica personalizada basada en screenWidth
  // Esto evita inconsistencias con react-device-detect al refrescar la página
  const shouldUseMobileLayout = isCustomTabletOrMobile;
  const shouldUseDesktopLayout = isCustomDesktop;

  // Breakpoints adicionales para mayor granularidad
  const isMobile = screenWidth < 768;
  const isTabletSize = screenWidth >= 768 && screenWidth < 1180;
  const isDesktopSize = screenWidth >= 1180;

  return {
    screenWidth,
    isCustomDesktop,
    isCustomTabletOrMobile,
    shouldUseMobileLayout,
    shouldUseDesktopLayout,
    isInitialized,
    forceUpdate, // Para forzar re-renders cuando sea necesario
    previousBreakpoint: previousBreakpointRef.current,
    // Breakpoints granulares
    isMobile,
    isTabletSize,
    isDesktopSize,
    // Mantener compatibilidad con react-device-detect
    isMobileOnly,
    isTablet,
    isDesktop,
  };
};

/**
 * Hook específico para detectar si debería usar layout móvil
 * Simplifica el uso cuando solo necesitas saber si es móvil o no
 */
export const useIsMobile = () => {
  const { shouldUseMobileLayout } = useResponsive();
  return shouldUseMobileLayout;
};

/**
 * Hook específico para detectar si debería usar layout desktop
 * Simplifica el uso cuando solo necesitas saber si es desktop o no
 */
export const useIsDesktop = () => {
  const { shouldUseDesktopLayout } = useResponsive();
  return shouldUseDesktopLayout;
};
